import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button, { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useTutorial } from "@/hooks/useTutorial";
import { AutoAdvanceDots } from "@/components/tutorial/AutoAdvanceDots";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { STEPS } from "@/components/tutorial/tutorialSteps";
import { TooltipContainer } from "@scripts/react/components/Tooltip.jsx";

export function TutorialCard() {
    const { currentStep, goTo, dismissCard, isCardDismissed } = useTutorial();
    const step = STEPS[currentStep];

    useEffect(() => {
        if (currentStep !== 0) {
            goTo(0);
        }
        setIsPlaying(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const NAVBAR_HEIGHT = 48;
    const TOP_OFFSET = NAVBAR_HEIGHT + 8;
    const LEFT_OFFSET = 8;
    const CARD_WIDTH = 420;
    const CARD_HEIGHT = 360;

    const [position, setPosition] = useState<{ left: number; top: number }>({
        left: LEFT_OFFSET,
        top: TOP_OFFSET
    });
    const [isPlaying, setIsPlaying] = useState(true);
    const dragOffsetRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
    const isDraggingRef = useRef(false);
    const prevDismissedRef = useRef<boolean>(isCardDismissed);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const shouldAutoPlayRef = useRef(true);

    const togglePlayPause = useCallback(() => {
        setIsPlaying((prev) => {
            const newIsPlaying = !prev;
            if (videoRef.current) {
                if (newIsPlaying) {
                    videoRef.current.play();
                } else {
                    videoRef.current.pause();
                }
            }
            return newIsPlaying;
        });
    }, []);

    const handleManualNavigation = useCallback(
        (step: number) => {
            shouldAutoPlayRef.current = true;
            setIsPlaying(true);
            goTo(step);
        },
        [goTo]
    );

    const handleVideoEnded = useCallback(() => {
        if (!isPlaying) return;

        if (currentStep >= STEPS.length - 1) {
            shouldAutoPlayRef.current = false;
            goTo(0);
            setIsPlaying(false);
        } else {
            shouldAutoPlayRef.current = true;
            goTo(currentStep + 1);
        }
    }, [isPlaying, currentStep, goTo]);

    useEffect(() => {
        const onResize = () => {
            const maxLeft = Math.max(0, window.innerWidth - CARD_WIDTH - 8);
            const maxTop = Math.max(0, window.innerHeight - CARD_HEIGHT - LEFT_OFFSET);
            setPosition((pos) => ({
                left: Math.min(Math.max(pos.left, LEFT_OFFSET), maxLeft),
                top: Math.min(Math.max(pos.top, TOP_OFFSET), maxTop)
            }));
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [TOP_OFFSET]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDraggingRef.current) return;
            const newLeft = e.clientX - dragOffsetRef.current.dx;
            const newTop = e.clientY - dragOffsetRef.current.dy;

            const maxLeft = Math.max(0, window.innerWidth - CARD_WIDTH - 8);
            const maxTop = Math.max(0, window.innerHeight - CARD_HEIGHT - LEFT_OFFSET);

            const clampedLeft = Math.min(Math.max(newLeft, LEFT_OFFSET), maxLeft);
            const clampedTop = Math.min(Math.max(newTop, TOP_OFFSET), maxTop);

            setPosition({ left: clampedLeft, top: clampedTop });
        };
        const handleMouseUp = () => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                document.body.style.userSelect = "";
                document.body.style.cursor = "";
            }
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [TOP_OFFSET]);

    useEffect(() => {
        const wasDismissed = prevDismissedRef.current;
        if (wasDismissed && !isCardDismissed) {
            setPosition({ left: LEFT_OFFSET, top: TOP_OFFSET });
            setIsPlaying(true);
            shouldAutoPlayRef.current = true;
            if (videoRef.current) {
                videoRef.current.currentTime = 0;
                videoRef.current.play();
            }
        }
        prevDismissedRef.current = isCardDismissed;
    }, [isCardDismissed, TOP_OFFSET]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            if (shouldAutoPlayRef.current) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    }, [currentStep]);

    useKeyboardShortcut({ key: "k", handler: togglePlayPause, disabled: isCardDismissed });

    const startDrag = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        isDraggingRef.current = true;
        dragOffsetRef.current = { dx: e.clientX - position.left, dy: e.clientY - position.top };
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
    };

    return (
        <AnimatePresence>
            {!isCardDismissed && (
                <motion.div
                    ref={cardRef}
                    className="group fixed z-50 flex cursor-move flex-col gap-3 rounded-xl border bg-white p-1 shadow-2xl"
                    style={{
                        left: position.left,
                        top: position.top,
                        width: CARD_WIDTH,
                        height: CARD_HEIGHT
                    }}
                    onMouseDown={startDrag}
                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{
                        duration: 0.1,
                        ease: "easeOut",
                        type: "spring",
                        stiffness: 300,
                        damping: 30
                    }}
                >
                    <div className="relative h-56 w-full flex-shrink-0 overflow-hidden rounded-t-lg bg-gray-100">
                        <video
                            ref={videoRef}
                            src={step.videoSrc}
                            className="absolute inset-0 h-full w-full object-cover"
                            muted
                            playsInline
                            controls={false}
                            onEnded={handleVideoEnded}
                        />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-gradient-to-t from-white to-white/0" />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white/50 to-white/0" />
                    </div>

                    <div className="flex flex-1 flex-col px-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep}
                                initial="hidden"
                                animate="visible"
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15, ease: "easeOut" }}
                                className="flex flex-col"
                            >
                                <motion.div
                                    className="flex items-center justify-between gap-2"
                                    variants={{
                                        hidden: { opacity: 0, y: 8, filter: "blur(5px)" },
                                        visible: { opacity: 1, y: 0, filter: "blur(0px)" }
                                    }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                >
                                    <h3 className="text-sm font-semibold text-primary">
                                        {step.title}
                                    </h3>
                                    <i
                                        className={twClassNames(
                                            step.icon,
                                            "text-base text-tertiary"
                                        )}
                                    />
                                </motion.div>

                                <motion.p
                                    className="text-sm text-tertiary"
                                    variants={{
                                        hidden: { opacity: 0, y: 8, filter: "blur(5px)" },
                                        visible: { opacity: 1, y: 0, filter: "blur(0px)" }
                                    }}
                                    transition={{ duration: 0.35, ease: "easeOut", delay: 0.1 }}
                                >
                                    {step.description}
                                </motion.p>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    <AutoAdvanceDots
                        stepsCount={STEPS.length}
                        index={currentStep}
                        onIndexChange={handleManualNavigation}
                    />
                    {/* @ts-expect-error - HoverDeleteButton type mismatch with JSX.Element */}
                    <HoverDeleteButton
                        onClick={dismissCard}
                        ariaLabel="Dismiss tutorial"
                        className="-right-1 -top-1"
                    />
                    <div className="absolute bottom-1 right-1">
                        <TooltipContainer
                            title={
                                <div>
                                    {isPlaying ? "Pause" : "Play"}
                                    <span className="hotkey dark ml-2">K</span>
                                </div>
                            }
                            type="titleOnly"
                            customButton={
                                <span>
                                    {/* @ts-expect-error - Button component type issue */}
                                    <Button
                                        onClick={togglePlayPause}
                                        aria-label={isPlaying ? "Pause" : "Play"}
                                        type="ghost"
                                        size="xsmall"
                                        shape="square"
                                    >
                                        <i
                                            className={twClassNames(
                                                "fas",
                                                isPlaying ? "fa-pause" : "fa-play"
                                            )}
                                        />
                                    </Button>
                                </span>
                            }
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
